import { GraphBuilder } from '../graph/builder';
import { ComponentDef } from './types';

export const lmHeadComponent: ComponentDef = {
  id: 'lm-head',
  name: 'Language Modeling Head',
  description: 'Project hidden states to vocabulary logits for next-token prediction.',
  category: 'component',
  params: [
    { name: 'd_model', label: 'Model dimension', type: 'number', default: 512, min: 64, max: 4096, step: 64 },
    { name: 'seq_len', label: 'Sequence length', type: 'number', default: 128, min: 1, max: 8192, step: 1 },
    { name: 'vocab_size', label: 'Vocab size', type: 'number', default: 32000, min: 1000, max: 200000, step: 1000 },
    {
      name: 'variant',
      label: 'Variant',
      type: 'select',
      default: 'untied',
      options: [
        { label: 'Untied (separate W_out)', value: 'untied' },
        { label: 'Tied Weights (share embedding)', value: 'tied' },
        { label: 'MoE Head (routed expert heads)', value: 'moe_head' }
      ]
    }
  ],
  buildGraph: (params) => {
    const d_model = Number(params.d_model);
    const seq_len = Number(params.seq_len);
    const vocab_size = Number(params.vocab_size);
    const variant = String(params.variant || 'untied');

    const g = new GraphBuilder();
    g.input('hidden', [seq_len, d_model]);

    if (variant === 'tied') {
      // Tied Weights: reuse the embedding weight matrix (transpose) for projection
      g.embedding('embed_table', 'hidden', [vocab_size, d_model]);
      g.transpose('w_tied', 'embed_table', [d_model, vocab_size]);
      g.matmul('logits', 'hidden', 'w_tied', [seq_len, vocab_size]);
      g.softmax('probs', 'logits', -1);
      g.output('out', 'probs');

    } else if (variant === 'moe_head') {
      // MoE Head: multiple expert heads each project to a vocab shard, routed by a gating network
      const numExperts = 4;
      const shardSize = Math.ceil(vocab_size / numExperts);

      g.router('router', 'hidden', [numExperts]);

      const expertIds: string[] = [];
      for (let i = 0; i < numExperts; i++) {
        const expertId = `expert_${i}`;
        g.linear(expertId, 'hidden', [d_model, shardSize]);
        expertIds.push(expertId);
      }

      g.concat('all_logits', expertIds, [seq_len, shardSize * numExperts]);
      g.mul('gated_logits', 'all_logits', 'router');
      g.softmax('probs', 'gated_logits', -1);
      g.output('out', 'probs');

    } else {
      // Untied (default): separate learned W_out projection to vocab
      g.linear('logits', 'hidden', [d_model, vocab_size]);
      g.softmax('probs', 'logits', -1);
      g.output('out', 'probs');
    }

    return g.build();
  }
};
